import L from "leaflet";

// ===============================
// CONSTANTS
// ===============================

// Base layers configuration
export const BASE_LAYERS = [
  {
    name: "CartoDB Light",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: "abcd",
    maxZoom: 20,
  },
  {
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap contributors",
    maxZoom: 19,
  },
  {
    name: "Satellite (Esri)",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
    maxZoom: 18,
  },
  /* {
    name: "CartoDB Dark",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: "© CartoDB",
  }, */
];

// Fix default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Excavation status colors (small circles)
const EXCAVATION_STATUS_COLORS = {
  active: "#22c55e", // green
  planned: "#eab308", // yellow
  completed: "#3b82f6", // blue
  suspended: "#ef4444", // red
  inactive: "#9ca3af", // gray
  "in-progress": "#3b82f6", // blue
  default: "#9ca3af", // gray
};

// Helper function to create excavation icon HTML (small circle)
const createExcavationIconHTML = (color) => `
  <div style="
    width: 14px;
    height: 14px;
    background-color: ${color};
    border: 2px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    position: relative;
  "></div>
`;

// Excavation icon configurations (Small circles)
const EXCAVATION_ICONS = Object.fromEntries(
  Object.entries(EXCAVATION_STATUS_COLORS).map(([status, color]) => [
    status,
    new L.DivIcon({
      className: "custom-excavation-marker",
      html: createExcavationIconHTML(color),
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      popupAnchor: [0, -7],
    }),
  ])
);

export const createCustomIcon = (status) => {
  const normalizedStatus = status?.toLowerCase() || "default";
  return EXCAVATION_ICONS[normalizedStatus] || EXCAVATION_ICONS.default;
};

// Validate and parse coordinates
export const validateCoordinates = (coordinates) => {
  if (!coordinates) return null;

  try {
    let coords;

    // If it's already an object
    if (typeof coordinates === "object") {
      coords = coordinates;
    } else {
      // Try to parse JSON string
      coords = JSON.parse(coordinates);
    }

    // Validate lat/lng
    if (
      coords &&
      typeof coords.lat === "number" &&
      typeof coords.lng === "number" &&
      coords.lat >= -90 &&
      coords.lat <= 90 &&
      coords.lng >= -180 &&
      coords.lng <= 180 &&
      !isNaN(coords.lat) &&
      !isNaN(coords.lng)
    ) {
      return coords;
    }
  } catch (error) {
    console.warn("Invalid coordinates format:", coordinates);
  }

  return null;
};

// Format date helper
export const formatDate = (dateString) => {
  if (!dateString) return "Not set";

  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid date";
  }
};

// Status colors mapping
export const STATUS_COLORS = {
  active: "bg-green-100 text-green-800 border-green-200",
  inactive: "bg-gray-100 text-gray-800 border-gray-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  "in-progress": "bg-blue-100 text-blue-800 border-blue-200",
  planned: "bg-purple-100 text-purple-800 border-purple-200",
  delayed: "bg-orange-100 text-orange-800 border-orange-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
};

// Get status color class
export const getStatusColorClass = (status) => {
  return STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS.inactive;
};

// Get status color classes (alias for consistency)
export const getStatusColor = (status) => {
  return getStatusColorClass(status);
};

// Calculate progress color
export const getProgressColor = (progress) => {
  if (progress >= 80) return "bg-green-500";
  if (progress >= 50) return "bg-blue-500";
  if (progress >= 30) return "bg-yellow-500";
  return "bg-orange-500";
};

// Filter excavations by search term
export const filterBySearchTerm = (excavations, searchTerm) => {
  if (!searchTerm) return excavations;

  const term = searchTerm.toLowerCase();
  return excavations.filter(
    (excavation) =>
      excavation.name?.toLowerCase().includes(term) ||
      excavation.description?.toLowerCase().includes(term) ||
      excavation.contractor?.toLowerCase().includes(term) ||
      excavation.Project?.name?.toLowerCase().includes(term)
  );
};

// Filter excavations by status
export const filterByStatus = (excavations, status) => {
  if (status === "all") return excavations;
  return excavations.filter((excavation) => excavation.status === status);
};

// Filter excavations by project
export const filterByProject = (excavations, projectName) => {
  if (projectName === "all") return excavations;
  return excavations.filter(
    (excavation) => excavation.Project?.name === projectName
  );
};

// Get unique projects from excavations
export const getUniqueProjects = (excavations) => {
  const projects = excavations
    .map((e) => e.Project?.name)
    .filter(Boolean)
    .filter((name, index, arr) => arr.indexOf(name) === index);

  return projects.sort();
};

// Calculate statistics from excavations
export const calculateStats = (excavations) => {
  return {
    total: excavations.length,
    active: excavations.filter((e) => e.status === "Active").length,
    planned: excavations.filter((e) => e.status === "Planned").length,
    completed: excavations.filter((e) => e.status === "Completed").length,
    suspended: excavations.filter((e) => e.status === "Suspended").length,
    averageDepth: excavations.length
      ? (
          excavations.reduce((sum, e) => sum + (e.depth || 0), 0) /
          excavations.filter((e) => e.depth).length
        ).toFixed(1)
      : 0,
    averageProgress: excavations.length
      ? (
          excavations.reduce((sum, e) => sum + (e.progress || 0), 0) /
          excavations.filter(
            (e) => e.progress !== null && e.progress !== undefined
          ).length
        ).toFixed(1)
      : 0,
  };
};

// Get unique status options from data
export const getUniqueStatuses = (data, field = "status") => {
  const statuses = data
    .map((item) => item[field])
    .filter((status) => status && status.trim() !== "")
    .map((status) => status.toLowerCase());

  return [...new Set(statuses)].sort();
};

// Filter data based on search term and status
export const filterData = (
  data,
  searchTerm,
  statusFilter,
  searchFields = ["name"]
) => {
  return data.filter((item) => {
    // Status filter
    const matchesStatus =
      statusFilter === "all" ||
      item.status?.toLowerCase() === statusFilter.toLowerCase();

    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      searchFields.some((field) => {
        const value = getNestedValue(item, field);
        return value && value.toLowerCase().includes(searchTerm.toLowerCase());
      });

    return matchesStatus && matchesSearch;
  });
};

// Get nested object value by dot notation
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Debounce function for search input
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Format number with thousand separators
export const formatNumber = (num) => {
  return new Intl.NumberFormat("id-ID").format(num);
};

// Get status display name
export const getStatusDisplayName = (status) => {
  const statusNames = {
    active: "Aktif",
    inactive: "Tidak Aktif",
    pending: "Menunggu",
    completed: "Selesai",
    cancelled: "Dibatalkan",
    "in-progress": "Sedang Berlangsung",
    planned: "Direncanakan",
    delayed: "Tertunda",
    suspended: "Ditangguhkan",
  };

  return statusNames[status?.toLowerCase()] || status || "Tidak Diketahui";
};
